import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import { Download, Share2, Mail, Edit, Loader2, ArrowLeft, MapPin, Hotel, Car, Calendar, FileText, CheckCircle, XCircle, CreditCard, AlertTriangle, Copy, Check, Link, FileWord, Image as ImageIcon, Layers, DollarSign, Send, Layout as LayoutIcon, Files, Calculator, RotateCw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '../contexts/TenantContext';
import { getAccessibleTextColor, getContrastTextColor } from '../utils/colorContrast';
import { trimCategoryNames } from '../utils/displayClean';
import TemplateSelector from '../components/TemplateSelector';
import { QuotationViewDoc } from '../components/QuotationView/QuotationViewDoc';
import CostAuditPanel from '../components/CostAuditPanel';
import { useAuth } from '../contexts/AuthContext';

import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { QuotationActionBar } from '../components/QuotationActionBar';

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { buildPath, tenant } = useTenant();
  const { isAdmin, hideVehicleCosts } = useAuth();

  // DMC-aware quotation theme: locally overrides --navy/--cyan (used by every
  // quotation-display component) with this agency's branding colors, falling
  // back to the OnTrOf defaults when unset. See UI Fix Brief (2026-02).
  const quotationThemeStyle = useMemo(() => {
    const style = {};
    if (tenant?.primary_color) style['--navy'] = getAccessibleTextColor(tenant.primary_color);
    if (tenant?.secondary_color) {
      style['--cyan'] = tenant.secondary_color;
      style['--cyan-strong'] = getAccessibleTextColor(tenant.secondary_color);
    }
    return style;
  }, [tenant?.primary_color, tenant?.secondary_color]);

  // The "Send WhatsApp" button fills solid with the accent color; a bright,
  // light accent (e.g. yellow) needs dark text instead of the usual white.
  const sendButtonTextColor = useMemo(
    () => getContrastTextColor(tenant?.secondary_color || '#00C8E8'),
    [tenant?.secondary_color]
  );
  const [showCostAudit, setShowCostAudit] = useState(false);
  const [quotation, setQuotation] = useState(null);
  const [quotationView, setQuotationView] = useState(null);
  useEffect(() => {
    if (!quotation?.quotation_id) return;
    api.get(`/quotations/${quotation.quotation_id}/view`, { params: { audience: 'guest', staff_tags: true } })
      .then((r) => setQuotationView(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not load the quotation view'));
  }, [quotation]);
  const [loading, setLoading] = useState(true);
  const [editingItinerary, setEditingItinerary] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [itineraryText, setItineraryText] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingFlyer, setDownloadingFlyer] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [cloning, setCloning] = useState(false);
  
  // Template selector state
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateOutputType, setTemplateOutputType] = useState('pdf'); // 'pdf' | 'docx' | 'email' | 'link'

  useEffect(() => {
    if (!id) return;
    setCurrentVersion(null);
    fetchQuotation();
  }, [id]);

  // Clone (new quote number) or New Version (same quote, next Vn): both re-open the wizard pre-filled.
  const startFromThisQuote = async (kind) => {
    try {
      setCloning(kind);
      const response = await api.post(`/quotations/${id}/${kind === 'version' ? 'new-version' : 'clone'}`);
      sessionStorage.setItem('clone_quotation_data', JSON.stringify(response.data));
      navigate(buildPath(`/quotations/create?${kind}=${encodeURIComponent(response.data.source_reference)}`));
    } catch (error) {
      toast.error(error.response?.data?.detail || (kind === 'version' ? 'Could not start a new version' : 'Could not clone quotation'));
    } finally {
      setCloning(false);
    }
  };

  const fetchQuotation = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await api.get(`/quotations/${id}`);
      setQuotation(trimCategoryNames(response.data));
      setItineraryText(response.data.itinerary_text || '');
    } catch (error) {
      toast.error('Failed to load quotation');
      navigate(buildPath('/quotations'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      const response = await api.post(`/quotations/${id}/regenerate`);
      if (response.data.success) {
        toast.success(response.data.message || 'Data regenerated successfully');
        setQuotation(trimCategoryNames(response.data.quotation));
      } else {
        toast.error('Failed to regenerate data');
      }
    } catch (error) {
      toast.error('Failed to regenerate data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRegenerating(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await api.patch(`/quotations/${id}/status`, { status: newStatus });
      toast.success('Status updated successfully');
      fetchQuotation();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const downloadPDF = async (theme = 'regular', templateId = null) => {
    try {
      let url = `/quotations/${id}/pdf?theme=${theme}`;
      if (templateId) url += `&template_id=${templateId}`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${quotation.quote_reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadWord = async (theme = 'regular', templateId = null) => {
    try {
      let url = `/quotations/${id}/docx?theme=${theme}`;
      if (templateId) url += `&template_id=${templateId}`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${quotation.quote_reference}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Word document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download Word document');
    }
  };

  const createShareLink = async (templateId = null) => {
    try {
      setCreatingLink(true);
      const response = await api.post(`/quotations/${id}/share-link`);
      const token = response.data.share_token;
      const fullUrl = `${window.location.origin}/quote/${token}`;
      setShareLink(fullUrl);
      setShowShareLinkModal(true);
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setCreatingLink(false);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle output with template selection
  const handleOutputWithTemplate = (outputType) => {
    setTemplateOutputType(outputType);
    setShowTemplateSelector(true);
  };

  // State for email template HTML
  const [emailTemplateHtml, setEmailTemplateHtml] = useState(null);
  const [emailText, setEmailText] = useState('');
  const [emailSubjectText, setEmailSubjectText] = useState('');
  const [loadingEmailHtml, setLoadingEmailHtml] = useState(false);

  // Handle template selection and trigger the output
  const handleTemplateSelected = async (templateId, theme = 'regular') => {
    switch (templateOutputType) {
      case 'pdf':
        downloadPDF(theme, templateId);
        break;
      case 'docx':
        downloadWord(theme, templateId);
        break;
      case 'link':
        createShareLink(templateId);
        break;
      case 'email':
        // Fetch rendered HTML from backend
        setLoadingEmailHtml(true);
        try {
          const link = quotation?.share_token ? `${window.location.origin}/quote/${quotation.share_token}` : undefined;
          const response = await api.get(`/quotations/${id}/email-html`, {
            params: { template_id: templateId || undefined, link, origin: window.location.origin, theme },
          });
          setEmailTemplateHtml(response.data.html);
          setEmailText(response.data.text || '');
          setEmailSubjectText(response.data.subject || '');
          setShowEmailModal(true);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to prepare email');
        }
        setLoadingEmailHtml(false);
        break;
      default:
        break;
    }
  };

  const downloadFlyer = async () => {
    try {
      setDownloadingFlyer(true);
      const response = await api.get(`/quotations/${id}/flyer`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quotation.quote_reference}_flyer.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Flyer image downloaded!');
    } catch (error) {
      toast.error('Failed to generate flyer');
    } finally {
      setDownloadingFlyer(false);
    }
  };

  const saveItinerary = async () => {
    try {
      console.log('Saving itinerary:', { quotation_id: id, itinerary_text: itineraryText?.substring(0, 100) });
      const response = await api.patch(`/quotations/${id}/itinerary`, { itinerary_text: itineraryText || '' });
      console.log('Save response:', response.data);
      toast.success('Itinerary updated successfully');
      setEditingItinerary(false);
      fetchQuotation();
    } catch (error) {
      console.error('Itinerary update error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      toast.error(error.response?.data?.detail || 'Failed to update itinerary');
    }
  };

  const copyToClipboard = (text, isHtml = false) => {
    const showSuccess = () => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    };
    
    const showError = () => {
      toast.error('Unable to copy. Please select and copy manually.');
    };
    
    if (isHtml) {
      // Strip HTML tags for plain text fallback (prevents Gmail showing raw code)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      // Use ClipboardItem API for proper HTML copy (works in Chrome/Firefox)
      if (typeof ClipboardItem !== 'undefined') {
        try {
          const htmlBlob = new Blob([text], { type: 'text/html' });
          const plainBlob = new Blob([plainText], { type: 'text/plain' });
          navigator.clipboard.write([
            new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob })
          ]).then(showSuccess).catch(showError);
        } catch (e) {
          // Fallback for any errors
          navigator.clipboard.writeText(text).then(showSuccess).catch(showError);
        }
      } else {
        // Safari fallback - ClipboardItem not supported
        // Copy as text (user will need to use a different browser for rich HTML)
        navigator.clipboard.writeText(text).then(showSuccess).catch(showError);
      }
    } else {
      navigator.clipboard.writeText(text).then(showSuccess).catch(showError);
    }
  };

  // Helper to clean overnight_at for display (handle legacy "Departure" text)
  const formatOvernightAt = (overnight) => {
    if (!overnight || overnight === 'Departure' || overnight.toLowerCase() === 'departure') {
      return null; // Return null for departure day - no overnight stay
    }
    return overnight;
  };

  // WhatsApp text is built on the server from the one Quotation View.
  const [whatsappText, setWhatsappText] = useState('');
  useEffect(() => {
    if (!quotation?.quotation_id) return;
    const link = quotation.share_token ? `${window.location.origin}/quote/${quotation.share_token}` : undefined;
    api.get(`/quotations/${quotation.quotation_id}/whatsapp-text`, { params: { link } })
      .then((r) => setWhatsappText(r.data.text || ''))
      .catch(() => setWhatsappText(''));
  }, [quotation, shareLink]);

  // Send WhatsApp message via WhatsApp Web
  const sendWhatsApp = () => {
    if (!quotation) return;
    
    // Get phone number and country code
    const phone = quotation.guest_phone;
    const countryCode = quotation.guest_country_code || '+91';
    
    if (!phone) {
      toast.error('No phone number available for this guest');
      return;
    }
    
    // Format phone number: remove any non-digit characters and add country code
    const cleanPhone = phone.replace(/\D/g, '');
    const cleanCountryCode = countryCode.replace(/\D/g, '');
    const fullPhone = cleanCountryCode + cleanPhone;
    
    // Generate the message text
    const message = whatsappText;
    
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp Web with pre-filled message
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${fullPhone}&text=${encodedMessage}`;
    
    // Open in new tab
    window.open(whatsappUrl, '_blank');
    
    toast.success('Opening WhatsApp Web...');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-navy" />
        </div>
      </Layout>
    );
  }

  if (!quotation) return null;

  return (
    <Layout>
      <div
        className="space-y-4 max-w-5xl mx-auto quotation-theme"
        style={quotationThemeStyle}
        data-testid="quotation-detail-page"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(buildPath('/quotations'))} size="sm" className="h-11" data-testid="quotation-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="quotation-reference">
                {quotation.quote_reference}
                {currentVersion && (
                  <span className="text-xs font-semibold rounded px-2 py-0.5 bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)]" data-testid="current-version-label">
                    {currentVersion.version_label}{currentVersion.is_latest ? ' · Latest' : ''}
                  </span>
                )}
              </h1>
              <p className="text-xs text-gray-600">Created {formatDate(quotation.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={quotation.status} onValueChange={updateStatus} disabled={updatingStatus}>
              <SelectTrigger className="w-32 h-11" data-testid="quotation-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="SENT">Sent</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <QuotationActionBar actions={[
          { id: 'download-pdf-btn', label: 'PDF', icon: Download, primary: true, title: 'Download PDF', onClick: () => handleOutputWithTemplate('pdf') },
          { id: 'download-docx-btn', label: 'Word', icon: FileText, title: 'Download Word', onClick: () => handleOutputWithTemplate('docx') },
          { id: 'whatsapp-text-btn', label: 'WhatsApp', icon: Share2, primary: true, title: 'Copy WhatsApp message', onClick: () => setShowWhatsAppModal(true) },
          { id: 'send-whatsapp-btn', label: 'Send WhatsApp', icon: Send, title: 'Send via WhatsApp Web', onClick: sendWhatsApp },
          { id: 'email-html-btn', label: 'Email', icon: Mail, title: 'Email content', onClick: () => handleOutputWithTemplate('email') },
          { id: 'share-link-btn', label: 'Link', icon: Link, primary: true, title: 'Create shareable link', onClick: () => handleOutputWithTemplate('link'), disabled: creatingLink, loading: creatingLink },
          { id: 'cost-audit-btn', label: 'Cost Audit', icon: Calculator, hidden: !isAdmin, title: 'Line-item cost calculation trace (internal)', onClick: () => setShowCostAudit(true) },
          { id: 'create-version-btn', label: 'Create New Version', icon: Layers, title: 'Create the next version of this quote, pre-filled from this version', onClick: () => startFromThisQuote('version'), disabled: !!cloning, loading: cloning === 'version' },
          { id: 'clone-quotation-btn', label: 'Clone', icon: Files, title: 'Start a brand-new quote (new number) copied from this one', onClick: () => startFromThisQuote('clone'), disabled: !!cloning, loading: cloning === 'clone' },
        ]} />

        <VersionHistoryPanel
          quotationId={id}
          onOpen={(vid) => navigate(buildPath(`/quotations/${vid}`))}
          onLoaded={(vs) => setCurrentVersion(vs.find((v) => v.is_current) || null)}
        />

        {/* QUOTATION CONTENT — the same server-built Quotation View as the link page and every document */}
        <div className="quotation-theme" style={quotationThemeStyle}>
          {quotationView ? <QuotationViewDoc view={quotationView} /> : (
            <div className="flex justify-center py-12" data-testid="quotation-view-loading"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          )}
        </div>


        {/* WhatsApp Modal */}
        <Dialog open={showWhatsAppModal} onOpenChange={setShowWhatsAppModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto quotation-theme" style={quotationThemeStyle}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[var(--navy)]" />
                WhatsApp Message
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Copy this text and paste in WhatsApp:</p>
              <pre className="bg-gray-100 p-4 rounded-lg text-sm whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto">
                {whatsappText || 'Preparing message…'}
              </pre>
              <Button onClick={() => copyToClipboard(whatsappText)} disabled={!whatsappText} className="w-full" data-testid="copy-whatsapp-text-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Modal */}
        <Dialog open={showEmailModal} onOpenChange={(open) => { setShowEmailModal(open); if (!open) setEmailTemplateHtml(null); }}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto quotation-theme" style={quotationThemeStyle}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[var(--navy)]" />
                Email Content
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Copy this HTML and paste in your email body:</p>
              {loadingEmailHtml ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <div className="border rounded-lg p-4 max-h-96 overflow-y-auto email-html-preview" dangerouslySetInnerHTML={{ __html: emailTemplateHtml || '' }} data-testid="email-html-preview" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => copyToClipboard(emailTemplateHtml || '', true)} className="flex-1" data-testid="copy-email-html-btn">
                      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? 'Copied!' : 'Copy HTML'}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const subject = encodeURIComponent(emailSubjectText);
                        const body = encodeURIComponent(`Dear ${quotation.guest_name || 'Guest'},\n\nPlease find below the quotation details:\n\n${emailText}`);
                        window.location.href = `mailto:${quotation.guest_email || ''}?subject=${subject}&body=${body}`;
                      }}
                      className="flex-1"
                      data-testid="open-email-app-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Open in Email App
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                    <Info className="w-3 h-3 shrink-0" />
                    Tip: "Copy HTML" works best on desktop browsers. For mobile, use "Open in Email App".
                  </p>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Link Modal */}
        <Dialog open={showShareLinkModal} onOpenChange={setShowShareLinkModal}>
          <DialogContent className="max-w-md quotation-theme" style={quotationThemeStyle}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link className="w-5 h-5 text-[var(--navy)]" />
                Shareable Link Created
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Share this link with your client. They can view the quotation without logging in.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border rounded-md bg-gray-50"
                  data-testid="share-link-input"
                />
                <Button onClick={copyShareLink} variant="outline" data-testid="copy-share-link-btn">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  className="flex-1" 
                  onClick={() => window.open(shareLink, '_blank')}
                  data-testid="open-share-link-btn"
                >
                  Open Link
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowShareLinkModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Template Selector Modal */}
        <TemplateSelector
          open={showTemplateSelector}
          onOpenChange={setShowTemplateSelector}
          onSelect={handleTemplateSelected}
          outputType={templateOutputType}
          themeStyle={quotationThemeStyle}
          accentTextColor={sendButtonTextColor}
        />

        {/* Cost Audit Panel — internal, agency admin only */}
        {isAdmin && (
          <CostAuditPanel
            quotationId={id}
            quoteReference={quotation?.quote_reference}
            open={showCostAudit}
            onOpenChange={setShowCostAudit}
          />
        )}
      </div>
    </Layout>
  );
};

export default QuotationDetail;
